<script setup>
  import { ref, computed, provide, toValue, onMounted, watchEffect, onErrorCaptured } from 'vue';

  import { useSpotStore } from '@/stores/SpotStore';
  import { useUserStore } from "@/stores/UserStore";
  import { useCompleteReportStore } from "@/stores/CompleteReportStore";
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import CompleteReportHeader from '@/components/CompleteReportHeader.vue'
  import CompleteReportContent from '@/components/CompleteReportContent.vue';
  import FavoriteSpotUserTip from "@/components/FavoriteSpotUserTip.vue";
  import TableOfContentsPills from '@/components/TableOfContentsPills.vue';
  import CompleteReportLastUpdateTime from '@/components/CompleteReportLastUpdateTime.vue';
  import ErrorBlock from '@/components/ErrorBlock.vue';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
  import useSpotGlobals from '@/composables/useSpotGlobals';
  import ModalSiteSettings from '@/components/ModalSiteSettings.vue';
  import ModalUserProfileManager from '@/components/ModalUserProfileManager.vue';
  import { useBugHandler } from '@/composables/useBugHandler';

  const { $WEB_VIEW, $PLATFORM, $GLOBAL_USER_PROFILE_MANAGER, $FEATURE_VERSION = 0 } = useSettingsGlobals();  
  const { $BRAND } = useConfigurationGlobals();
  const { $ID, $LAT, $LON, $IS_SPOT, $IS_FAVORITE_SPOT, $IS_ARBITRARY_SPOT } = useSpotGlobals();

  const spotStore = useSpotStore();
  const userStore = useUserStore();
  const completeReportStore = useCompleteReportStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');
  const isSmallDevice = useMediaQuery('(max-width: 840px)');

  const { report } = useBugHandler();

  const spotId = ref($ID);
  const lat = ref($LAT)
  const lon = ref($LON)
  const data = ref(null);
  const isSpot = ref($IS_SPOT);
  const isFavoriteSpot = ref($IS_FAVORITE_SPOT);
  const isArbitrarySpot = ref($IS_ARBITRARY_SPOT);
  const loadFooterAd = ref(false);
  const tableOfContents = ref(null);
  const spotLoaded = ref(false);
  const contentContainer = ref(null);

  const showHeader = computed(() => !completeReportStore.error && !$WEB_VIEW);
  const showLastUpdateTime = computed(() => !completeReportStore.error && !completeReportStore.loading);
  const showTableOfContents = computed(() => !completeReportStore.error && $WEB_VIEW && !completeReportStore.loading && spotLoaded.value);
  const showUserTip = computed(() => !completeReportStore.error && userStore.memberLevel > 0 && ($WEB_VIEW || isSmallDevice.value));
  const pageClasses = computed(() => ({ 'jwx-page--web-view': $WEB_VIEW }));
  const platformParamAvailable = computed(() => !$WEB_VIEW || $WEB_VIEW && $FEATURE_VERSION >= 1.0);
  const tocStyle = computed(() => $FEATURE_VERSION >= 1.1 ? 'floatingPills' : 'block');

  if (!isArbitrarySpot.value) {
    provide('isNCWind', toValue(spotStore.isNCWind))
  }

  const fetchSpotDetail = async () => {
    try {
      if (isSpot.value) {
        data.value = await spotStore.getSpotDetailById(spotId.value, { spot_types: "1,100,101" });
      }
    
      if (isFavoriteSpot.value) {
        data.value = await spotStore.getSpotDetailByFavoriteId(spotId.value, { spot_types: "1,100,101" });
      }
    
      if (isArbitrarySpot.value) {
        data.value = await spotStore.getSpotDetailByLatLon(`${lat.value}:${lon.value}`, { spot_types: "1,100,101" });
      }
  
      if (data.value.error) throw data.value;
  
    } catch(e) {
      console.warn('There was an error retrieving the spot data.', e);
  
      completeReportStore.error = e.errorObject || {
        header: 'Sorry, there was a problem.',
        title: 'The complete report could not be loaded for this spot.',
        message: `Please try again later.`,
        errorText: e.message
      }
    }
  }

  const setPageDatasets = () => {
    const body = document.documentElement;

    if ($WEB_VIEW) {
      body.dataset.webView = true;

      body.dataset.featureVersion = $FEATURE_VERSION;
      body.dataset.brand = $BRAND.toLowerCase();

      if (platformParamAvailable.value) {
        body.dataset.platform = $PLATFORM.toLowerCase();
      }
    }
  }     

  onMounted(async () => {
    // Add touch class to html element if not desktop size. Does not have to do with touch events.
    if (!isDesktop.value) document.documentElement.classList.add('touch');

    await userStore.initialize();
    await userStore.getProfiles();
    await userStore.getAPIStorage();

    await fetchSpotDetail();

    setPageDatasets();
    
    spotLoaded.value = true;
    loadFooterAd.value = true;

    // Make sure the table of contents reflects the proper order and visiblity of the containers
    tableOfContents.value?.rebuild();
  });

  watchEffect(() => {
    // Make sure the table of contents reflects the proper order and visiblity of the containers after updates to settings
    if (!completeReportStore.showSettingsModal) {
      tableOfContents.value?.rebuild()
    }
  })

  onErrorCaptured((e) => {
    report(e, { 
      component: 'CompleteReport',
      spotId: spotId.value,
      lat: lat.value,
      lon: lon.value,
      data: data.value,
      isSpot: isSpot.value,
      isFavoriteSpot: isFavoriteSpot.value,
      isArbitrarySpot: isArbitrarySpot.value,
      completeReportStoreError: completeReportStore.error,
    });    

    return false;
  })     
</script>

<template>
  <template v-if="userStore.initialized && spotStore.spot">
    <TableOfContentsPills
      v-if="showTableOfContents"
      ref="tableOfContents"
      :contentContainer="contentContainer"
      :style="tocStyle"
    />
    <div
      class="jwx-page"
      :class="pageClasses"
    >
      <div ref="pageContainer" class="jwx-page__container" >
        <ErrorBlock v-if="completeReportStore.error" :error="completeReportStore.error" />
        
        <CompleteReportHeader v-if="showHeader" />
        <CompleteReportLastUpdateTime v-if="showLastUpdateTime" />
        <FavoriteSpotUserTip v-if="showUserTip" :floating="false" />
        <CompleteReportContent v-model:contentContainer="contentContainer" />
      </div>
    </div>
  </template>
  <Suspense>
    <ModalSiteSettings :show="userStore.showSettingsModal" />
  </Suspense>

  <ModalUserProfileManager
    v-if="$GLOBAL_USER_PROFILE_MANAGER && userStore.userProfiles"
    v-model:currentProfileId="userStore.currentProfileId"
    :userProfiles="userStore.userProfiles.profiles"
  />  
</template>